import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, Renderer2, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { I18nService } from '../../../services/i18n.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { ApprovalStatusComponent } from '../../items/approval/approval-status/approval-status.component';
import { ApprovalService } from '../../items/approval/services/approval.service';
import { AuthService } from '../../../services/auth.service';
import { environment } from '../../../services/global-environment';

export interface ApprovalStep {
  stepDefinitionId: string; // 步驟定義ID
  level:string; // 步驟級別
  stepName: string;
  approverName?: string;
  approverNames?: string[];
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  comment?: string;
  actionDate?: string;
  // 新增：支援點擊功能的屬性
  clickable?: boolean;
  stepIndex?: number;
  approverIds?: string[]; // 簽核人員ID列表，用於權限檢查
}

@Component({
  selector: 'app-approval-flow-display',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ApprovalStatusComponent],
  templateUrl: './approval-flow-display.component.html',
  styleUrls: ['./approval-flow-display.component.scss']
})
export class ApprovalFlowDisplayComponent implements OnInit, OnDestroy {
  @Input() steps: ApprovalStep[] = [];
  @Input() compact: boolean = false; // 是否使用緊湊模式
  @Input() showLabels: boolean = true; // 是否顯示步驟標籤
  @Input() enableStepClick: boolean = true; // 是否啟用步驟點擊功能
  @Input() currentUserId: string = ''; // 當前使用者ID，用於權限檢查
  @Input() deleted: boolean = false; // 🔒 項目是否已被廢止或停用

  // 新增：審核相關的輸入參數
  @Input() id: string = ''; // 資料本身的id
  @Input() formId: string = ''; // 表單ID
  @Input() formName: string = ''; // 表單名稱
  @Input() approvalFlow: any[] = []; // 簽核流程資料
  @Input() approvalResult: any = null; // 簽核結果資料

  // 輸出事件：當步驟被點擊時
  @Output() stepClick = new EventEmitter<{step: ApprovalStep, index: number}>();

  // 新增：審核完成事件
  @Output() approvalComplete = new EventEmitter<any>();

  // 審核狀態顯示相關屬性
  showApprovalStatus = false;
  selectedFormId = '';
  selectedFormName = '';
  selectedapprovalflow: any[] = [];

  // 單一階段簽核相關屬性
  showSingleStepApproval = false;
  selectedStep: any = null;
  selectedStepIndex: number = -1;
  selectedStepDefinitionId: string = ''; // 新增：用於識別當前簽核步驟的ID

  // 新增：載入和錯誤狀態
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  // 環境變数を公開してテンプレートからアクセス可能にする
  environment = environment;

  constructor(
    private i18nService: I18nService,
    private approvalService: ApprovalService,
    private authService: AuthService,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {

    // console.log('ApprovalFlowDisplayComponent 初期化 - steps 資料:', this.steps);
    // console.log('ApprovalFlowDisplayComponent 初期化 - 其他屬性:', {
    //   compact: this.compact,
    //   showLabels: this.showLabels,
    //   enableStepClick: this.enableStepClick,
    //   currentUserId: this.currentUserId,
    //   formId: this.formId,
    //   formName: this.formName,
    //   approvalFlow: this.approvalFlow,
    //   approvalResult: this.approvalResult
    // });
  }

  /**
   * 取得步驟狀態的CSS類別（根據簽核結果更新）
   */
  getStepStatusClass(step: ApprovalStep, index: number): string {
    const baseClass = 'step-node';

    // 優先從 approvalFlow 中取得狀態資料
    if (this.approvalFlow && this.approvalFlow.length > index) {
      const approvalFlowStep = this.approvalFlow[index];
      if (approvalFlowStep && approvalFlowStep.status) {
        switch (approvalFlowStep.status) {
          case 'approved':
            return `${baseClass} step-approved`;
          case 'in_progress':
            return `${baseClass} step-in-progress`;
          case 'rejected':
            return `${baseClass} step-rejected`;
          case 'pending':
          default:
            return `${baseClass} step-pending`;
        }
      }
    }

    // 如果有簽核結果資料，作為第二選擇
    if (this.approvalResult && this.approvalResult.stepResults) {
      const stepResult = this.getStepResult(index);
      if (stepResult && stepResult.completed) {
        return stepResult.action === 'approve' ? `${baseClass} step-approved` : `${baseClass} step-rejected`;
      } else if (index === this.getCurrentStep()) {
        return `${baseClass} step-in-progress`;
      }
    }

    // 最後回到原始 steps 的邏輯
    switch (step.status) {
      case 'approved':
        return `${baseClass} step-approved`;
      case 'in_progress':
        return `${baseClass} step-in-progress`;
      case 'rejected':
        return `${baseClass} step-rejected`;
      case 'pending':
      default:
        return `${baseClass} step-pending`;
    }
  }

  /**
   * 取得步驟節點的CSS類別（包含點擊狀態）
   */
  getStepNodeClass(step: ApprovalStep, index: number): string {
    const baseClass = this.getStepStatusClass(step, index);
    const clickableClass = this.isStepClickable(step, index) ? 'step-clickable' : '';
    return `${baseClass} ${clickableClass}`.trim();
  }

  /**
   * 根據簽核結果取得指定步驟的結果
   */
  private getStepResult(stepIndex: number): any {
    return this.approvalResult?.stepResults?.find(
      (result: any) => result.stepIndex === stepIndex
    );
  }

  /**
   * 取得當前進行中的步驟索引
   */
  private getCurrentStep(): number {
    return this.approvalResult?.currentStep || 0;
  }

  /**
   * 取得連接線的CSS類別
   */
  getConnectorClass(step: ApprovalStep, nextStep: ApprovalStep | null, index: number): string {
    if (!nextStep) return '';

    // 優先從 approvalFlow 取得狀態
    let currentStatus = step.status;
    if (this.approvalFlow && this.approvalFlow.length > index) {
      const approvalFlowStep = this.approvalFlow[index];
      if (approvalFlowStep && approvalFlowStep.status) {
        currentStatus = approvalFlowStep.status;
      }
    }

    // 如果當前步驟已完成（approved），連接線為綠色
    if (currentStatus === 'approved') {
      return 'connector-completed';
    }

    // 如果當前步驟進行中，連接線為黃色
    if (currentStatus === 'in_progress') {
      return 'connector-in-progress';
    }

    // 如果當前步驟被拒絕，連接線為紅色
    if (currentStatus === 'rejected') {
      return 'connector-rejected';
    }

    // 其他情況為灰色
    return 'connector-pending';
  }

  /**
   * 取得步驟狀態文字
   */
  getStepStatusText(step: ApprovalStep, index?: number): string {
    // 優先從 approvalFlow 中取得狀態
    let status = step.status;
    if (typeof index === 'number' && this.approvalFlow && this.approvalFlow.length > index) {
      const approvalFlowStep = this.approvalFlow[index];
      if (approvalFlowStep && approvalFlowStep.status) {
        status = approvalFlowStep.status;
      }
    }

    switch (status) {
      case 'approved':
        return this.i18nService.translate('approval.status.approved');
      case 'in_progress':
        return this.i18nService.translate('approval.status.in_progress');
      case 'rejected':
        return this.i18nService.translate('approval.status.rejected');
      case 'pending':
      default:
        return this.i18nService.translate('approval.status.pending');
    }
  }

  /**
   * 取得簽核者名稱
   */
  getApproverNames(step: ApprovalStep, index?: number): string {
    // 優先從 approvalFlow 中取得簽核人資料
    if (typeof index === 'number' && this.approvalFlow && this.approvalFlow.length > index) {
      const approvalFlowStep = this.approvalFlow[index];
      if (approvalFlowStep && approvalFlowStep.approverList && approvalFlowStep.approverList.length > 0) {
        return approvalFlowStep.approverList.map((approver: any) => approver.name).join(', ');
      }
    }

    // 回到原始邏輯
    if (step.approverNames && step.approverNames.length > 0) {
      return step.approverNames.join(', ');
    }
    if (step.approverName) {
      return step.approverName;
    }
    return this.i18nService.translate('approval.step.unspecified_approver');
  }

  /**
   * 建立步驟的tooltip內容
   */
  getStepTooltip(step: ApprovalStep, index: number): string {
    const stepNumber = index + 1;

    // 優先從 approvalFlow 取得步驟名稱
    let stepName = step.stepName;
    if (this.approvalFlow && this.approvalFlow.length > index && this.approvalFlow[index].stepName) {
      stepName = this.approvalFlow[index].stepName;
    } else if (!stepName) {
      stepName = this.i18nService.translate('approval.step.step_number', { number: stepNumber });
    }

    const approverNames = this.getApproverNames(step, index);
    const status = this.getStepStatusText(step, index);

    let tooltip = `${this.i18nService.translate('approval.step.name')}: ${stepName}\n`;
    tooltip += `${this.i18nService.translate('approval.step.approver')}: ${approverNames}\n`;
    tooltip += `${this.i18nService.translate('approval.step.status')}: ${status}`;

    if (step.actionDate) {
      const actionDate = new Date(step.actionDate).toLocaleString();
      tooltip += `\n${this.i18nService.translate('approval.step.process_time')}: ${actionDate}`;
    }

    if (step.comment) {
      tooltip += `\n${this.i18nService.translate('approval.step.comment')}: ${step.comment}`;
    }

    // 檢查是否可點擊，並添加相應提示
    if (this.isStepClickable(step, index)) {
      tooltip += `\n\n${this.i18nService.translate('approval.step.click_to_view')}`;
    } else {
      // 獲取步驟狀態，優先使用 approvalFlow 中的狀態
      let stepStatus = step.status;
      if (this.approvalFlow && this.approvalFlow[index]) {
        const flowStep = this.approvalFlow[index] as any;
        stepStatus = flowStep.status || step.status;
      }

      if (stepStatus === 'pending') {
        tooltip += `\n\n${this.i18nService.translate('approval.step.pending_not_clickable')}`;
      } else {
        tooltip += `\n\n${this.i18nService.translate('approval.step.not_clickable')}`;
      }
    }

    return tooltip;
  }

  /**
   * 取得步驟圖示
   * @deprecated 此方法已不再使用，現在使用數字顯示而非圖示
   */
  getStepIcon(step: ApprovalStep): string {
    switch (step.status) {
      case 'approved':
        return 'fas fa-check';
      case 'in_progress':
        return 'fas fa-clock';
      case 'rejected':
        return 'fas fa-times';
      case 'pending':
      default:
        return 'fas fa-circle';
    }
  }

  /**
   * 處理步驟點擊事件
   */
  onStepClick(step: ApprovalStep, index: number): void {
    if (this.enableStepClick && this.isStepClickable(step, index)) {
      // 如果有提供表單資訊，直接開啟審核視窗
      if (this.formId && this.formName) {
        this.openSingleStepApproval(step, index);
      } else {
        // 否則發出事件讓父元件處理
        this.stepClick.emit({step, index});
      }
    }
  }

  /**
   * 檢查步驟是否可點擊
   */
  isStepClickable(step: ApprovalStep, index: number): boolean {
    // 🔒 安全檢查：已廢止或停用的項目，禁止所有步驟點擊
    if (this.deleted) {
      return false;
    }

    if (!this.enableStepClick) {
      return false;
    }

    // 獲取步驟狀態，優先使用 approvalFlow 中的狀態
    let stepStatus = step.status;
    if (this.approvalFlow && this.approvalFlow[index]) {
      const flowStep = this.approvalFlow[index] as any;
      stepStatus = flowStep.status || step.status;
    }

    // 待處理和被拒絕的步驟不可點擊
    if (stepStatus === 'pending' || stepStatus === 'rejected') {
      return false;
    }

    // 只有進行中和已核准的步驟需要檢查權限
    if (stepStatus === 'in_progress' || stepStatus === 'approved') {
      // 檢查使用者是否在簽核人員名單中
      if (this.currentUserId) {
        return this.hasApprovalPermission(step, index);
      }
      return true; // 如果沒有提供 currentUserId，保持原有行為
    }

    return false;
  }

  /**
   * 檢查使用者是否有簽核權限
   */
  private hasApprovalPermission(step: ApprovalStep, index: number): boolean {
    if (!this.currentUserId) {
      return false;
    }

    // 優先從 approvalFlow 中取得簽核人員清單
    if (this.approvalFlow && this.approvalFlow[index]) {
      const flowStep = this.approvalFlow[index] as any;
      if (flowStep.approverList && Array.isArray(flowStep.approverList)) {
        return flowStep.approverList.some((approver: any) =>
          approver.id === this.currentUserId
        );
      }
    }

    // 檢查 step 中的 approverIds 清單
    if (step.approverIds && Array.isArray(step.approverIds)) {
      return step.approverIds.includes(this.currentUserId);
    }

    // 如果沒有簽核人員清單，預設不允許點擊
    return false;
  }

  /**
   * 開啟單一階段簽核介面
   */
  openSingleStepApproval(step: ApprovalStep, stepIndex: number): void {
    // 251026-問題1: 驗證當前使用者是否為該步驟的簽核者
    if (!this.hasApprovalPermission(step, stepIndex)) {
      const currentUserId = this.getCurrentUserId();
      console.warn('⚠️ [openSingleStepApproval] 當前使用者無權簽核此步驟', {
        currentUserId,
        stepName: step.stepName,
        stepApprovers: step.approverIds
      });

      alert(this.i18nService.translate('approval.error.not_authorized') ||
            '您不是此步驟的簽核者，無法進行簽核。');
      return;
    }

    this.selectedFormId = this.formId;
    this.selectedFormName = this.formName;
    this.selectedStepIndex = stepIndex;
    this.selectedapprovalflow = this.approvalFlow;
    this.selectedStepDefinitionId = step.stepDefinitionId || '';

    // 使用 stepDefinitionId 從 approvalFlow 中尋找對應的步驟資料
    if (this.approvalFlow && this.approvalFlow.length > 0 && step.stepDefinitionId) {
      const foundStep = this.approvalFlow.find(flowStep => flowStep.stepDefinitionId === step.stepDefinitionId);
      if (foundStep) {
        this.selectedStep = foundStep;
      } else {
        this.errorMessage = this.i18nService.translate('approval.status.step_not_found', { stepId: step.stepDefinitionId });
        return;
      }
    } else if (!step.stepDefinitionId) {
      this.errorMessage = this.i18nService.translate('approval.status.missing_step_definition_id');
      return;
    } else {
      this.errorMessage = this.i18nService.translate('approval.status.missing_approval_flow_data');
      return;
    }

    // bodyにクラスを追加してスクロールを無効化
    this.renderer.addClass(document.body, 'modal-open');
    
    // 親のtr.disabled要素を探してモーダル表示中クラスを追加
    const hostElement = this.elementRef.nativeElement;
    const parentRow = hostElement.closest('tr.disabled');
    if (parentRow) {
      this.renderer.addClass(parentRow, 'modal-active');
    }
    
    // 直接顯示模態框，移除不必要的 setTimeout 延遲
    this.showSingleStepApproval = true;
  }

  /**
   * 關閉簽核狀態視窗
   */
  closeApprovalStatus(): void {
    this.showApprovalStatus = false;
    this.selectedFormId = '';
    this.selectedFormName = '';
    this.selectedapprovalflow = [];
  }

  /**
   * 關閉單一階段簽核視窗
   */
  closeSingleStepApproval(): void {
    // bodyのクラスを削除してスクロールを復元
    this.renderer.removeClass(document.body, 'modal-open');
    
    // 親のtr.disabled.modal-activeからクラスを削除
    const hostElement = this.elementRef.nativeElement;
    const parentRow = hostElement.closest('tr.disabled.modal-active');
    if (parentRow) {
      this.renderer.removeClass(parentRow, 'modal-active');
    }
    
    this.showSingleStepApproval = false;
    this.selectedStep = null;
    this.selectedStepIndex = -1;
    this.selectedFormId = '';
    this.selectedFormName = '';
    this.selectedapprovalflow = [];
  }  /**
   * 簽核完成回調
   */
  // onApprovalComplete(approvalData: any): void {


  //   // 提取當前簽核階段的資料
  //   // const currentStepData = this.extractCurrentStepData(approvalData);

  //   // 提交簽核
  //   // this.submitApproval(currentStepData);
  // }

  // /**
  //  * 提取當前簽核階段的資料
  //  */
  // private extractCurrentStepData(approvalData: any): any {

  //   console.log('提取當前簽核階段資料:', approvalData);

  //   try {
  //     // 找到當前簽核的步驟
  //     const currentStep = approvalData.steps.find((step: any) =>
  //       step.stepDefinitionId === approvalData.stepDefinitionId // 或根據步驟定義ID
  //     );

  //     console.log('當前簽核步驟:', currentStep);

  //     if (!currentStep) {
  //       console.error('找不到當前簽核步驟');
  //       return null;
  //     }

  //     console.log('this.approvalFlow:', this.approvalFlow);

  //     // 找到對應的原始簽核流程定義
  //     const originalStep = this.approvalFlow.find(
  //       (originalStep: any) => originalStep.stepDefinitionId === currentStep.stepDefinitionId
  //     );

  //     // 修正 approver.userName 和 userId - 使用當前登入使用者的資訊
  //     const currentUserInfo = this.authService.getUserInfo();
  //     let correctedApprover = {
  //       ...currentStep.approver,
  //       userName: currentUserInfo.username,
  //       userId: currentUserInfo.userid
  //     };

  //     // 整合 customFormData 到 customFormItems 中
  //     let integratedCustomFormItems = originalStep?.customFormItems || [];
  //     if (currentStep.customFormData && integratedCustomFormItems.length > 0) {
  //       integratedCustomFormItems = integratedCustomFormItems.map((item: any) => {
  //         const formDataValue = currentStep.customFormData[item.name] || currentStep.customFormData[item.id];
  //         return {
  //           ...item,
  //           value: formDataValue !== undefined ? formDataValue : item.value
  //         };
  //       });
  //     }

  //     // 整合當前步驟的完整資料
  //     const integratedCurrentStep = {
  //       // 保留簽核執行結果的資料
  //       stepDefinitionId: currentStep.stepDefinitionId,
  //       name: currentStep.name,
  //       order: currentStep.order,
  //       approver: correctedApprover, // 使用修正後的 approver 資訊
  //       status: currentStep.status,
  //       comment: currentStep.comment,
  //       signature: currentStep.signature,
  //       actionDate: currentStep.actionDate,
  //       actionResult: currentStep.actionResult,

  //       // 整合原始資料庫格式的資料
  //       level: originalStep?.level || currentStep.order,
  //       stepName: originalStep?.stepName || currentStep.name,
  //       approverType: originalStep?.approverType ,
  //       customFormItems: integratedCustomFormItems, // 使用整合後的自訂表單項目

  //       // 其他可能的原始欄位 (都很有參考價值，但目前都用不到，先註解起來)
  //       ...(originalStep && {
  //         // timeLimit: originalStep.timeLimit,
  //         // isParallel: originalStep.isParallel,
  //         // requiredAll: originalStep.requiredAll,
  //         // description: originalStep.description,
  //         // formConfig: originalStep.formConfig,
  //         // requireComment: originalStep.requireComment,
  //         // requireSignature: originalStep.requireSignature,
  //         // optional: originalStep.optional,
  //         // userId: originalStep.userId,
  //         // userName: originalStep.userName,
  //         // isDynamicDepartment: originalStep.isDynamicDepartment,
  //         approverList: originalStep.approverList
  //       })
  //     };

  //     // 取得下一個步驟的名稱
  //     const nextStepIndex = this.selectedStepIndex + 1;
  //     let nextStepName = "";
  //     let nextStepid = "";

  //     if (nextStepIndex < this.approvalFlow.length) {
  //       const nextStep = this.approvalFlow[nextStepIndex];
  //       nextStepName = nextStep?.stepName;
  //       nextStepid = nextStep?.stepDefinitionId;
  //     }

  //     // 構建只包含當前步驟的簽核資料跟下一個步驟的資料的物件
  //     const currentStepData = {
  //       id: approvalData.id,
  //       flowDefinitionId: approvalData.flowDefinitionId,
  //       formId: approvalData.formId,
  //       formTypeId: approvalData.formTypeId,
  //       initiator: approvalData.initiator,
  //       status: approvalData.status,
  //       currentStepIndex: this.selectedStepIndex,
  //       updated_step: integratedCurrentStep, // 只包含當前步驟
  //       next_step_name: nextStepName, //下一個步驟的名稱
  //       next_step_stepDefinitionId : nextStepid, //下一個步驟的ID
  //       createdAt: approvalData.createdAt,
  //       updatedAt: approvalData.updatedAt
  //     };

  //     return currentStepData;
  //   } catch (error) {
  //     console.error('提取當前簽核階段資料時發生錯誤:', error);
  //     return null;
  //   }
  // }

  // /**
  //  * 整合簽核資料與原始資料庫格式
  //  */
  // private integrateApprovalDataWithOriginalFormat(approvalData: any): any {
  //   try {
  //     // 複製原始簽核資料
  //     const integratedData = { ...approvalData };

  //     // 如果有原始的簽核流程資料，進行整合
  //     if (this.approvalFlow && this.approvalFlow.length > 0) {
  //       integratedData.steps = approvalData.steps.map((executedStep: any, index: number) => {
  //         // 找到對應的原始簽核流程定義
  //         const originalStep = this.approvalFlow.find(
  //           (originalStep: any) => originalStep.level === executedStep.order
  //         ) || this.approvalFlow[index];

  //         // 整合兩種格式的資料
  //         return {
  //           // 保留簽核執行結果的資料
  //           stepDefinitionId: executedStep.stepDefinitionId,
  //           name: executedStep.name,
  //           order: executedStep.order,
  //           approver: executedStep.approver,
  //           status: executedStep.status,
  //           comment: executedStep.comment,
  //           signature: executedStep.signature,
  //           actionDate: executedStep.actionDate,
  //           customFormData: executedStep.customFormData,
  //           actionResult: executedStep.actionResult,

  //           // 整合原始資料庫格式的資料
  //           level: originalStep?.level || executedStep.order,
  //           stepName: originalStep?.stepName || executedStep.name,
  //           approverType: originalStep?.approverType || 'specific_user',
  //           customFormItems: originalStep?.customFormItems || [],

  //           // 其他可能的原始欄位
  //           ...(originalStep && {
  //             approverIds: originalStep.approverIds,
  //             approverNames: originalStep.approverNames,
  //             conditions: originalStep.conditions,
  //             timeLimit: originalStep.timeLimit,
  //             isParallel: originalStep.isParallel,
  //             requiredAll: originalStep.requiredAll,
  //             description: originalStep.description,
  //             formConfig: originalStep.formConfig
  //           })
  //         };
  //       });
  //     }

  //     return integratedData;
  //   } catch (error) {
  //     console.error('整合簽核資料時發生錯誤:', error);
  //     // 如果整合失敗，返回原始資料
  //     return approvalData;
  //   }
  // }

  // /**
  //  * 提交簽核到後端
  //  */
  // private submitApproval(approvalData: any): void {
  //   if (!this.formId) {
  //     console.error('缺少表單ID，無法提交簽核');
  //     this.errorMessage = '缺少表單ID，無法提交簽核';
  //     return;
  //   }

  //   this.isSubmitting = true;
  //   this.errorMessage = '';

  //   const formData = {
  //     id: this.id, // 資料本身的ID
  //     formId: this.formId,
  //     companyid: this.getCompanyId(),
  //     stepIndex: this.selectedStepIndex,
  //     approvalData: approvalData
  //   };

  //   console.log('提交簽核資料到後端:', formData);

  //   this.approvalService.submitApprovalAndUpdateForm(formData).subscribe({
  //     next: (response) => {
  //       console.log('簽核提交成功:', response);
  //       this.handleApprovalSuccess(response);
  //     },
  //     error: (error) => {
  //       console.error('簽核提交失敗:', error);
  //       this.handleApprovalError(error);
  //     },
  //     complete: () => {
  //       this.isSubmitting = false;
  //     }
  //   });
  // }

  /**
   * 處理簽核成功
   */
  handleApprovalSuccess(response: any): void {
    console.log('Processing approval success:', response);

    // 顯示成功訊息
    this.successMessage = this.i18nService.translate('approval.status.approval_completed');

    // 更新本地的簽核結果資料
    this.updateLocalApprovalData(response);

    // 關閉彈出視窗（延遲關閉以顯示成功訊息）
    setTimeout(() => {
      this.closeApprovalStatus();
      this.closeSingleStepApproval();
    }, 1500);

    // 通知父元件簽核完成
    this.approvalComplete.emit(response);
  }




  /**
   * 處理簽核失敗
   */
  private handleApprovalError(error: any): void {
    console.error('處理簽核失敗:', error);

    // 顯示錯誤訊息
    this.errorMessage = error.message || error.error?.message || '簽核提交失敗，請稍後再試';
  }

  /**
   * 更新本地簽核資料
   */
  private updateLocalApprovalData(response: any): void {
    // 如果後端回傳了更新後的簽核結果，更新本地資料
    if (response && response.approvalResult) {
      this.approvalResult = response.approvalResult;
      console.log('更新本地簽核資料:', this.approvalResult);
    }
  }

  /**
   * 取得當前使用者ID
   */
  private getCurrentUserId(): string {
    const userInfo = this.authService.getUserInfo();
    return userInfo.userid || this.currentUserId || 'unknown-user';
  }

  /**
   * 取得當前使用者名稱
   */
  private getCurrentUserName(): string {
    const userInfo = this.authService.getUserInfo();
    return userInfo.username || 'Unknown User';
  }

  /**
   * 取得公司ID
   */
  private getCompanyId(): string {
    return environment.companyid || 'unknown-company';
  }

  /**
   * コンポーネント破棄時にbody要素のスクロールを復元
   */
  ngOnDestroy(): void {
    // モーダルが開いたままの場合、bodyのスクロールを復元
    if (this.showSingleStepApproval || this.showApprovalStatus) {
      this.renderer.removeClass(document.body, 'modal-open');
    }
  }
}
